import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, CanMatchFn, Router } from '@angular/router';

import { AuthStore } from './auth-store';
import type { Capability } from '../data/staff-roles';

/**
 * Gate on every console route: waits for the auth store's first resolved state
 * (claims included), then requires SOME Blume staff role.
 *
 * - Signed out -> `/sign-in`.
 * - Signed in with no role -> the polite `/not-authorised` screen. An
 *   `@blumemethod.com` address alone lands here: the domain is not authority,
 *   so a teammate must be granted a role (and sign in again) first.
 *
 * Any role opens the console; WHICH parts they get is the capability table's
 * job, applied per surface (see {@link requireCapability} and the sub-tab
 * filtering in dashboards.page.ts / database.page.ts).
 *
 * This is UX, not security - it only picks the screen. The Firestore rules and
 * the Callables are what actually refuse a read or a write.
 *
 * Works as CanActivate, CanActivateChild and CanMatch - it only ever returns
 * `true` or a redirect UrlTree, never `false`.
 */
export const adminGuard: CanActivateFn & CanActivateChildFn & CanMatchFn = async () => {
  const store = inject(AuthStore);
  const router = inject(Router);

  await store.whenReady();

  if (!store.user()) return router.parseUrl('/sign-in');
  if (!store.isStaff()) return router.parseUrl('/not-authorised');
  return true;
};

/**
 * Gate a route on ONE capability. Deny by default: a route that needs more than
 * "is staff" says so here rather than letting the page render and fail.
 *
 * Sends a staff member who lacks it back to `/dashboards` rather than to
 * `/not-authorised` - they ARE authorised, just not for this. Bouncing them to
 * a screen that says "you do not have access to the console" would be a lie.
 *
 * Stacks with {@link adminGuard}, which runs first as the shell's
 * canActivateChild and has already awaited `whenReady()`; this one awaits it
 * too, so it is correct standalone (as a `canMatch`, where the child guard has
 * not necessarily run).
 */
export function requireCapability(
  capability: Capability,
): CanActivateFn & CanActivateChildFn & CanMatchFn {
  return async () => {
    const store = inject(AuthStore);
    const router = inject(Router);

    await store.whenReady();

    if (!store.user()) return router.parseUrl('/sign-in');
    if (!store.isStaff()) return router.parseUrl('/not-authorised');
    return store.can(capability) ? true : router.parseUrl('/dashboards');
  };
}

/**
 * Gate a route on ANY ONE of several capabilities.
 *
 * For a page that hosts two unrelated tools - /seed-data carries both the mock
 * data seeder (mockdata.write) and the launch import (launch.populate), which
 * gate themselves once inside. Holding either is reason enough to open it;
 * holding neither is not.
 */
export function requireAnyCapability(
  ...capabilities: readonly Capability[]
): CanActivateFn & CanActivateChildFn & CanMatchFn {
  return async () => {
    const store = inject(AuthStore);
    const router = inject(Router);

    await store.whenReady();

    if (!store.user()) return router.parseUrl('/sign-in');
    if (!store.isStaff()) return router.parseUrl('/not-authorised');
    return capabilities.some((capability) => store.can(capability))
      ? true
      : router.parseUrl('/dashboards');
  };
}
